export const FONT = "'JetBrains Mono', 'Courier New', monospace";

export function arrowHead(x1, y1, x2, y2, len = 12, w = 6) {
  const a = Math.atan2(y2 - y1, x2 - x1);
  const c = Math.cos(a);
  const s = Math.sin(a);
  return `${x2},${y2} ${x2 - len * c + w * s},${y2 - len * s - w * c} ${x2 - len * c - w * s},${y2 - len * s + w * c}`;
}

export function Txt({ x, y, children, color, size = 12, halo = "#050B10", anchor = "start", weight = 700 }) {
  return (
    <text x={x} y={y} fill={color} fontSize={size} fontFamily={FONT} fontWeight={weight} textAnchor={anchor}
      stroke={halo} strokeWidth={size / 4} paintOrder="stroke" strokeLinejoin="round">
      {children}
    </text>
  );
}

export function Arrow({ x1, y1, x2, y2, color, width, dash }) {
  const a = Math.atan2(y2 - y1, x2 - x1);
  const d = Math.hypot(x2 - x1, y2 - y1);
  const len = Math.min(3 * width + 7, Math.max(d * 0.75, 5));
  const cut = Math.min(len * 0.7, d);
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2 - cut * Math.cos(a)} y2={y2 - cut * Math.sin(a)} stroke={color} strokeWidth={width} strokeLinecap="round" strokeDasharray={dash} />
      <polygon points={arrowHead(x1, y1, x2, y2, len, len * 0.5)} fill={color} />
    </g>
  );
}

export function ShapeView({ s, halo = "#050B10" }) {
  const fs = s.fs || 1;
  const { x1, y1, x2, y2, color: c, width: w } = s;
  switch (s.type) {
    case "line": case "hline": case "vline": case "dline":
      return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth={w} strokeLinecap="round" />;
    case "arrow":
      return <Arrow x1={x1} y1={y1} x2={x2} y2={y2} color={c} width={w} />;
    case "axisX": case "axisY": case "axisZ":
      return (
        <g>
          <Arrow x1={x1} y1={y1} x2={x2} y2={y2} color={c} width={w} />
          <Txt x={x2 + 8 * fs} y={y2 - 6 * fs} color={c} size={16 * fs} halo={halo}>{s.type.slice(-1)}</Txt>
        </g>
      );
    case "circle":
      return <circle cx={x1} cy={y1} r={Math.hypot(x2 - x1, y2 - y1)} fill="none" stroke={c} strokeWidth={w} />;
    case "rect":
      return <rect x={Math.min(x1, x2)} y={Math.min(y1, y2)} width={Math.abs(x2 - x1)} height={Math.abs(y2 - y1)} fill="none" stroke={c} strokeWidth={w} />;
    case "point":
      return (
        <g>
          <circle cx={x1} cy={y1} r={9 * fs} fill="none" stroke={c} strokeWidth={2 * fs} />
          <circle cx={x1} cy={y1} r={4 * fs} fill={c} />
          {s.text && <Txt x={x1 + 12 * fs} y={y1 - 10 * fs} color={c} size={13 * fs} halo={halo}>{s.text}</Txt>}
        </g>
      );
    case "text":
      return <Txt x={x1} y={y1} color={c} size={15 * fs} halo={halo}>{s.text || "Texto"}</Txt>;
    case "cota": {
      const a = Math.atan2(y2 - y1, x2 - x1);
      const nx = -Math.sin(a) * 8 * fs;
      const ny = Math.cos(a) * 8 * fs;
      const mx = (x1 + x2) / 2;
      const my = (y1 + y2) / 2;
      return (
        <g>
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth={w} />
          <line x1={x1 - nx} y1={y1 - ny} x2={x1 + nx} y2={y1 + ny} stroke={c} strokeWidth={w} />
          <line x1={x2 - nx} y1={y2 - ny} x2={x2 + nx} y2={y2 + ny} stroke={c} strokeWidth={w} />
          <polygon points={arrowHead(x2, y2, x1, y1, 10 * fs, 4 * fs)} fill={c} />
          <polygon points={arrowHead(x1, y1, x2, y2, 10 * fs, 4 * fs)} fill={c} />
          {s.text && <Txt x={mx - nx * 1.6} y={my - ny * 1.6} color={c} size={13 * fs} halo={halo} anchor="middle">{s.text}</Txt>}
        </g>
      );
    }
    case "anomaly":
      return (
        <g>
          <polygon points={`${x1},${y1 - 15 * fs} ${x1 - 13 * fs},${y1 + 8 * fs} ${x1 + 13 * fs},${y1 + 8 * fs}`} fill={c} stroke={halo} strokeWidth={1.5 * fs} />
          <text x={x1} y={y1 + 5 * fs} fill="#FFFFFF" fontSize={13 * fs} fontWeight={900} fontFamily={FONT} textAnchor="middle">!</text>
          {s.text && <Txt x={x1 + 16 * fs} y={y1 + 4 * fs} color={c} size={13 * fs} halo={halo}>{s.text}</Txt>}
        </g>
      );
    default:
      return null;
  }
}

export function ShapesLayer({ shapes = [], selectedId, onSelect, halo }) {
  return (
    <g>
      {shapes.map((s) => (
        <g key={s.id} data-testid={onSelect ? `shape-${s.id}` : undefined} style={onSelect ? { cursor: "pointer" } : undefined}
          onPointerDown={onSelect ? (e) => { e.stopPropagation(); onSelect(s.id); } : undefined}>
          {s.id === selectedId && <g opacity={0.45}><ShapeView s={{ ...s, color: "#FFD500", width: (s.width || 2) + 6 }} halo={halo} /></g>}
          <ShapeView s={s} halo={halo} />
        </g>
      ))}
    </g>
  );
}
